/**
 * MyTXT's motion vocabulary.
 *
 * A few named ingredients, used everywhere, so movement reads as one product:
 *
 *  OPTIC    something arriving into focus: quick start, long quiet settle.
 *  TRAVEL   an object deliberately relocating (the brand moving to its corner).
 *  RECEDE   something leaving: eases away, never snaps.
 *  SPRING_UI      controls and small surfaces: crisp, ~1 % overshoot.
 *  SPRING_SURFACE larger surfaces (menus, palette): no visible overshoot.
 *  Window springs run in JS (see `Spring`) so they can start from the
 *  pointer's velocity and be retargeted mid-flight.
 *
 * Springs are compiled to CSS `linear()` easings and exposed as custom
 * properties, so stylesheets and WAAPI share exactly the same curves.
 */

export const reducedMotionQuery = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
export const isReduced = () => reducedMotionQuery?.matches ?? false;

export const OPTIC = 'cubic-bezier(0.2, 0.72, 0.1, 1)';
export const TRAVEL = 'cubic-bezier(0.64, 0, 0.16, 1)';
export const RECEDE = 'cubic-bezier(0.42, 0, 0.78, 0.36)';

/** Damped harmonic oscillator, unit mass, from 0 to 1 with initial velocity 0. */
function springCurve(stiffness: number, damping: number) {
  const w0 = Math.sqrt(stiffness);
  const zeta = damping / (2 * w0);
  const x = (t: number) => {
    if (zeta < 1) {
      const wd = w0 * Math.sqrt(1 - zeta * zeta);
      return 1 - Math.exp(-zeta * w0 * t) * (Math.cos(wd * t) + ((zeta * w0) / wd) * Math.sin(wd * t));
    }
    return 1 - Math.exp(-w0 * t) * (1 + w0 * t);
  };
  let settle = 0;
  for (let t = 0; t < 3; t += 0.004) if (Math.abs(1 - x(t)) > 0.002) settle = t;
  const n = 40;
  const points: string[] = [];
  for (let i = 0; i <= n; i++) points.push((i === n ? 1 : x((i / n) * settle)).toFixed(4));
  return { linear: `linear(${points.join(', ')})`, ms: Math.round(settle * 1000) };
}

const supportsLinear = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('transition-timing-function', 'linear(0, 1)');

function spring(stiffness: number, damping: number, fallback: string) {
  const s = springCurve(stiffness, damping);
  return { css: supportsLinear ? s.linear : fallback, ms: s.ms };
}

export const SPRING_UI = spring(420, 34, OPTIC);
export const SPRING_SURFACE = spring(300, 34, OPTIC);
export const SPRING_SOFT = spring(170, 26, OPTIC);

/** Publish the vocabulary to CSS once, before the first frame. */
export function installMotionTokens(root: HTMLElement = document.documentElement): void {
  const set = (name: string, value: string) => root.style.setProperty(name, value);
  set('--spring-ui', SPRING_UI.css);
  set('--spring-ui-ms', `${SPRING_UI.ms}ms`);
  set('--spring-surface', SPRING_SURFACE.css);
  set('--spring-surface-ms', `${SPRING_SURFACE.ms}ms`);
  set('--spring-soft', SPRING_SOFT.css);
  set('--spring-soft-ms', `${SPRING_SOFT.ms}ms`);
}

/**
 * Mark a pointer gesture in progress on the page (`move`, `tab`, `resize-…`),
 * so every element shows the same cursor and nothing is selected; null ends it.
 */
export function setGesture(name: string | null): void {
  const root = document.documentElement;
  if (name) root.dataset.gesture = name;
  else delete root.dataset.gesture;
}

// --- interruptible WAAPI ----------------------------------------------------

const TAG = 'mytxt';

/** Freeze our running animations on `el` at their current value. */
function settle(el: Element) {
  for (const a of el.getAnimations()) {
    if (a.id !== TAG) continue;
    try {
      a.commitStyles();
    } catch {
      // Not rendered; nothing to keep.
    }
    a.cancel();
  }
}

interface Opts {
  duration: number;
  easing?: string;
  delay?: number;
}

/**
 * Animate `el` between keyframes. Interrupting is always safe: a running
 * animation is committed where it is and the new one continues from there.
 * The end state is written inline, then the animation is dropped.
 */
export function play(el: Element | null | undefined, frames: Keyframe[], { duration, easing = OPTIC, delay = 0 }: Opts): Promise<void> {
  if (!el || typeof (el as HTMLElement).animate !== 'function') return Promise.resolve();
  settle(el);
  const end = frames[frames.length - 1];
  if (isReduced()) duration = Math.min(duration, 1);
  const a = el.animate(frames, { duration, easing, delay, fill: 'both' });
  a.id = TAG;
  return a.finished.then(
    () => {
      const style = (el as HTMLElement).style;
      for (const [k, v] of Object.entries(end)) {
        if (k === 'offset' || k === 'easing' || k === 'composite' || v == null) continue;
        style.setProperty(k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`), String(v));
      }
      a.cancel();
    },
    () => {},
  );
}

// --- springs in JS ----------------------------------------------------------

/**
 * A critically-tuned spring over several numbers at once (a window's x, y,
 * width and height). It can start with a velocity — a released window keeps
 * its momentum into the snap — and can be retargeted while moving.
 */
export class Spring {
  private value: number[];
  private velocity: number[];
  private target: number[];
  private frame = 0;
  private last = 0;
  private resolve: (() => void) | null = null;

  constructor(
    initial: number[],
    private onFrame: (value: number[]) => void,
    private stiffness = 380,
    private damping = 38,
  ) {
    this.value = [...initial];
    this.velocity = initial.map(() => 0);
    this.target = [...initial];
  }

  get current(): number[] {
    return [...this.value];
  }

  get moving(): boolean {
    return this.frame !== 0;
  }

  /** Jump without animating. */
  set(value: number[]): void {
    this.stop();
    this.value = [...value];
    this.target = [...value];
    this.velocity = value.map(() => 0);
    this.onFrame(this.value);
  }

  to(target: number[], options: { velocity?: number[]; stiffness?: number; damping?: number } = {}): Promise<void> {
    this.target = [...target];
    if (options.velocity) this.velocity = [...options.velocity];
    if (options.stiffness) this.stiffness = options.stiffness;
    if (options.damping) this.damping = options.damping;
    if (isReduced()) {
      this.set(target);
      return Promise.resolve();
    }
    const done = new Promise<void>((r) => {
      this.resolve?.();
      this.resolve = r;
    });
    if (!this.frame) {
      this.last = performance.now();
      this.frame = requestAnimationFrame(this.step);
    }
    return done;
  }

  stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.resolve?.();
    this.resolve = null;
  }

  private step = (now: number) => {
    // Fixed sub-steps keep the integration stable on slow frames.
    let dt = Math.min(0.064, (now - this.last) / 1000);
    this.last = now;
    const h = 1 / 240;
    let resting = true;
    while (dt > 0) {
      const s = Math.min(h, dt);
      dt -= s;
      for (let i = 0; i < this.value.length; i++) {
        const force = -this.stiffness * (this.value[i] - this.target[i]) - this.damping * this.velocity[i];
        this.velocity[i] += force * s;
        this.value[i] += this.velocity[i] * s;
      }
    }
    for (let i = 0; i < this.value.length; i++) {
      if (Math.abs(this.value[i] - this.target[i]) > 0.2 || Math.abs(this.velocity[i]) > 2) resting = false;
    }
    if (resting) {
      this.value = [...this.target];
      this.velocity = this.value.map(() => 0);
      this.onFrame(this.value);
      this.frame = 0;
      this.resolve?.();
      this.resolve = null;
      return;
    }
    this.onFrame(this.value);
    this.frame = requestAnimationFrame(this.step);
  };
}

// --- pulses -----------------------------------------------------------------

/**
 * A short gesture that starts and ends at an element's resting style (an icon
 * answering a press). Nothing is committed: repeating it restarts it cleanly,
 * and the element's own state transitions are never fought.
 */
export function pulse(el: Element | null | undefined, frames: Keyframe[], { duration, easing = OPTIC, delay = 0 }: Opts): void {
  if (!el || typeof (el as HTMLElement).animate !== 'function' || isReduced()) return;
  for (const a of el.getAnimations()) if (a.id === 'mytxt-pulse') a.cancel();
  const a = el.animate(frames, { duration, easing, delay, fill: 'backwards' });
  a.id = 'mytxt-pulse';
}
